import type { PageContext } from '../lib/types';
// Community Gallery — work made with Content Factory flows. Every piece links to the flow behind it;
// when that flow is on the Marketplace it can be bought right from here.
import { html } from '../lib/dom';
import * as store from '../store/ui';
import { icon } from '../components/icons';
import { thumb, coverThumb } from '../components/thumb';
import { pageHead, tabs, searchInput, empty, avatar } from '../components/ui';
import { workCard, priceTag, stars, creatorLine, likeButton, listingStatus, bindCommunity } from '../components/community';
import { openPurchase, openShareWork } from '../components/marketplace-modals';
import { listGallery, getGalleryItem, getCreator, getListing, isForSale, isOwnCreator, isOwnListing, creatorWorks, listingWorks, purchaseOf } from '../api/marketplace';
import { getFlow } from '../api/flows';
import { num, relTime } from '../lib/format';

const SORTS = [['trending', 'Trending'], ['newest', 'Newest'], ['liked', 'Most liked'], ['saved', 'Liked by you']];
const KINDS = [['', 'All'], ['image', 'Images'], ['video', 'Video'], ['character', 'Characters']];

export function renderList(ctx: PageContext) {
  const q = ctx.query;
  const sort = SORTS.some(([k]: any) => k === q.tab) ? q.tab : 'trending';
  const rows = listGallery({ q: q.q, kind: q.kind, sort: sort === 'saved' ? 'newest' : sort, forSale: q.sale === '1', likedIds: sort === 'saved' ? store.get().likes : undefined });

  const body = html`<div class="page">
    ${pageHead({
      eyebrow: 'Community', title: 'Gallery',
      lede: 'Work made with Content Factory flows. Open the flow behind any piece — and if it’s on the Marketplace, buy it and run it on your own brand.',
      actions: html`<a class="btn" href="#/marketplace">${icon('tag')}Marketplace</a><button class="btn primary" data-share>${icon('plus')}Share your work</button>`,
    })}
    ${tabs(SORTS, sort)}
    <div class="toolbar">
      ${searchInput(q.q, 'Search work, creators, prompts…')}
      <div class="type-chips">${KINDS.map(([k, l]: any) => html`<button class="${(q.kind || '') === k ? 'on' : ''}" data-param="kind" data-value="${k}">${l}</button>`)}</div>
      <span class="spacer"></span>
      <div class="type-chips"><button class="${q.sale === '1' ? 'on' : ''}" data-param="sale" data-value="${q.sale === '1' ? '' : '1'}">${icon('tag')} Flow for sale</button></div>
    </div>
    ${rows.length ? html`<div class="masonry">${rows.map(workCard)}</div>` : empty(sort === 'saved' ? 'Like work to keep it here.' : 'Nothing matches these filters.')}
  </div>`;

  return {
    title: 'Gallery', body,
    mount(root?: any) {
      bindCommunity(root, ctx);
      root.querySelector('[data-share]')?.addEventListener('click', () => openShareWork());
    },
  };
}

function madeWith(it?: any, l?: any, flow?: any) {
  if (l && isForSale(l)) {
    const own = isOwnListing(l);
    const bought = purchaseOf(l.id);
    return html`<section class="card pad col" style="gap:12px">
      <div class="eyebrow">Made with a flow on the Marketplace</div>
      <a class="row" href="#/marketplace/${l.id}" style="gap:12px">${coverThumb(l, 'thumb mini')}<div class="col" style="gap:3px;min-width:0;flex:1"><strong class="ellipsis" style="font-weight:500">${l.title}</strong><span class="row mono muted" style="font-size:11px;gap:6px">${l.rating ? html`${stars(l.rating)} ${l.rating.toFixed(1)} ·` : ''} ${num(l.sales_count)} sales · ${l.node_count} nodes</span></div>${priceTag(l.price)}</a>
      <div class="row">
        ${own ? html`<a class="btn" href="#/marketplace/${l.id}">Your listing</a>`
          : bought ? html`<a class="btn" href="#/marketplace/${l.id}">${icon('check')}Purchased</a>`
            : html`<button class="btn primary" data-buy>${icon('bag')}${l.price ? `Buy flow · ${priceTag(l.price)}` : 'Get flow for free'}</button>`}
        <a class="btn ghost" href="#/marketplace/${l.id}">What’s inside</a>
      </div>
    </section>`;
  }
  if (l && isOwnListing(l)) {
    return html`<section class="card pad col" style="gap:10px"><div class="eyebrow">Your listing</div><div class="row" style="gap:10px"><a href="#/marketplace/${l.id}" style="font-weight:500">${l.title}</a>${listingStatus(l)}</div><p class="muted" style="font-size:13px">Not visible to buyers until it’s live.</p></section>`;
  }
  if (flow) {
    return html`<section class="card pad col" style="gap:10px"><div class="eyebrow">Made with your flow</div><a class="btn" href="#/projects/${flow.project_id}/flows/${flow.id}">${icon('node')}Open ${flow.name}</a></section>`;
  }
  return html`<section class="card pad"><div class="eyebrow" style="margin-bottom:6px">Flow</div><p class="muted" style="font-size:13px">The creator hasn’t put this flow on sale.</p></section>`;
}

export function renderDetail(ctx: PageContext) {
  const it = getGalleryItem(ctx.params.itemId);
  if (!it) return { title: 'Not found', body: html`<div class="page">${empty('This work was removed.', html`<a class="btn sm" href="#/gallery">Back to Gallery</a>`)}</div>` };
  const c = getCreator(it.creator_id);
  const l = it.listing_id ? getListing(it.listing_id) : null;
  const flow = isOwnCreator(c) && it.flow_id ? getFlow(it.flow_id) : null;
  const sameFlow = l && isForSale(l) ? listingWorks(l.id).filter((x?: any) => x.id !== it.id).slice(0, 8) : [];
  const more = creatorWorks(c.id, it.id).slice(0, 8);
  const following = store.isFollowing(c.id);

  const body = html`<div class="page">
    <a class="btn ghost sm" href="#/gallery" style="align-self:flex-start">${icon('back')}Gallery</a>
    <div class="work-detail">
      <div class="work-media" style="aspect-ratio:${it.aspect}">
        ${thumb(it.thumbnail, { label: it.title })}
        ${it.kind === 'video' ? html`<span class="kind-badge">${icon('play')}</span>` : ''}
      </div>
      <aside class="col" style="gap:16px">
        <div><div class="eyebrow">${it.kind}</div><h1 style="font-size:28px">${it.title}</h1></div>
        <div class="row" style="gap:10px">
          ${avatar(c)}
          <div class="col" style="gap:0;min-width:0;flex:1"><span class="row" style="gap:6px"><strong style="font-weight:500">${c.name}</strong>${c.verified ? html`<span class="verified" title="Verified creator">${icon('check')}</span>` : ''}</span><span class="mono muted" style="font-size:11px">@${c.handle} · ${num(c.followers + (following ? 1 : 0))} followers</span></div>
          ${isOwnCreator(c) ? html`<span class="chip">You</span>` : html`<button class="btn sm ${following ? '' : 'primary'}" data-follow="${c.id}">${following ? 'Following' : 'Follow'}</button>`}
        </div>
        <div class="row wrap mono muted" style="gap:14px;font-size:11.5px">${likeButton(it)}<span>${num(it.views)} views</span>${it.remixes ? html`<span>${num(it.remixes)} remixes</span>` : ''}<span>${relTime(it.created_at)}</span></div>
        ${madeWith(it, l, flow)}
        ${it.prompt ? html`<section class="col" style="gap:6px"><span class="eyebrow">Prompt</span><pre class="code" style="white-space:pre-wrap">${it.prompt}</pre></section>` : ''}
        ${it.models.length ? html`<section class="col" style="gap:6px"><span class="eyebrow">Models</span><div class="row wrap" style="gap:6px">${it.models.map((m?: any) => html`<span class="chip">${m}</span>`)}</div></section>` : ''}
      </aside>
    </div>
    ${sameFlow.length ? html`<section class="section"><div class="section-head"><h2>More made with this flow</h2><a class="link" href="#/marketplace/${l.id}">View flow →</a></div><div class="masonry">${sameFlow.map(workCard)}</div></section>` : ''}
    ${more.length ? html`<section class="section"><div class="section-head"><h2>More from ${creatorLine(c)}</h2></div><div class="masonry">${more.map(workCard)}</div></section>` : ''}
  </div>`;

  return {
    title: it.title, body,
    mount(root?: any) {
      bindCommunity(root, ctx);
      root.querySelector('[data-buy]')?.addEventListener('click', () => openPurchase(l));
    },
  };
}
